import {
  EMAIL_PATTERN,
  PHONE_COUNTRY_CODE,
  PHONE_DIGITS_PATTERN,
} from '../../../src/constants/contactValidationPatterns';
import type { CustomFieldType, FieldId, FormField } from './types';

/**
 * The embed bundle cannot import the platform's Yup schemas, so only the dependency-free
 * patterns are shared (see src/constants/contactValidationPatterns.ts). A submission rejected
 * by the widget must also be rejected by the API.
 */
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
// `T` only: the API validates this field as strict ISO 8601, which rejects a space separator.
const DATETIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const DOLLARS_PATTERN = /^\d+(\.\d{1,2})?$/;

export { PHONE_COUNTRY_CODE };

/**
 * Drops formatting and the country code so the remaining digits can be length checked,
 * matching normalizePhoneNumber in the platform.
 */
export const normalizePhoneDigits = (value: string): string => {
  let digits = value.replace(/[\s\-().]/g, '').replace(/^\+1/, '').replace(/^\+/, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits;
};

export const isValidPhoneNumber = (value: string): boolean =>
  PHONE_DIGITS_PATTERN.test(normalizePhoneDigits(value.trim()));

/** Submits the number the way the platform API expects it: +1 followed by the ten digits. */
export const toSubmittablePhoneNumber = (value: string): string => {
  const digits = normalizePhoneDigits(value.trim());
  return PHONE_DIGITS_PATTERN.test(digits) ? `${PHONE_COUNTRY_CODE}${digits}` : value.trim();
};

export const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim());

/** Rejects calendar-invalid dates such as 2026-02-31, which the pattern alone would allow. */
const isRealDate = (year: number, month: number, day: number): boolean => {
  if (month < 1 || month > 12 || day < 1) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

export const isValidDate = (value: string): boolean => {
  const parts = DATE_PATTERN.exec(value.trim());
  if (!parts) return false;
  return isRealDate(Number(parts[1]), Number(parts[2]), Number(parts[3]));
};

export const isValidDateTime = (value: string): boolean => {
  const parts = DATETIME_PATTERN.exec(value.trim());
  if (!parts) return false;
  if (!isRealDate(Number(parts[1]), Number(parts[2]), Number(parts[3]))) return false;
  const hours = Number(parts[4]);
  const minutes = Number(parts[5]);
  const seconds = parts[6] === undefined ? 0 : Number(parts[6]);
  return hours < 24 && minutes < 60 && seconds < 60;
};

export const isValidNumber = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed !== '' && Number.isFinite(Number(trimmed));
};

export const isValidDollars = (value: string): boolean =>
  DOLLARS_PATTERN.test(value.trim().replace(/^\$/, '').replace(/,/g, ''));

const platformFieldError = (fieldId: FieldId, value: string): string | null => {
  if (fieldId === 'mobilePhone') {
    return isValidPhoneNumber(value)
      ? null
      : `Enter a 10-digit US mobile number, for example ${PHONE_COUNTRY_CODE} 555 123 4567.`;
  }
  if (fieldId === 'email') return isValidEmail(value) ? null : 'Enter a valid email address.';
  if (fieldId === 'birthdate') return isValidDate(value) ? null : 'Enter a valid date.';
  return null;
};

const customFieldError = (fieldType: CustomFieldType, value: string): string | null => {
  if (fieldType === 'number') return isValidNumber(value) ? null : 'Enter a number.';
  if (fieldType === 'dollars') {
    return isValidDollars(value) ? null : 'Enter a dollar amount, for example 25 or 25.50.';
  }
  if (fieldType === 'date') return isValidDate(value) ? null : 'Enter a valid date.';
  if (fieldType === 'datetime') return isValidDateTime(value) ? null : 'Enter a valid date and time.';
  return null;
};

/** Returns the message to show under the input, or null when the value is acceptable. */
export const validateFormField = (field: FormField, value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return field.required ? `${field.label} is required.` : null;
  if (field.fieldId) return platformFieldError(field.fieldId, trimmed);
  if (field.customFieldType) return customFieldError(field.customFieldType, trimmed);
  return null;
};

/** Field name to error message, for every field that failed. Empty when the form can submit. */
export const validateFormFields = (
  fields: FormField[],
  values: Record<string, string>,
): Record<string, string> => fields.reduce<Record<string, string>>((errors, field) => {
  const message = validateFormField(field, values[field.name] ?? '');
  if (message) errors[field.name] = message;
  return errors;
}, {});
